import { useState, type ReactElement } from "react";
import { Navigate } from "react-router";
import { Button } from "@heroui/react";
import { useAuthContext } from "@/features/authentication/useAuthContext";
import { signOut } from "@/services/auth";

export const Pending = (): ReactElement => {
    const { user, loading } = useAuthContext();
    const [signingOut, setSigningOut] = useState(false);

    const handleSignOut = async () => {
        setSigningOut(true);
        try {
            await signOut();
        } catch {
            // The Navigate below sends us to /login once the session clears;
            // only reset on error, since there's nothing to recover otherwise.
            setSigningOut(false);
        }
    };

    // /pending is public, so the route guard never unmounts us when the session
    // ends. Redirect ourselves once there's no signed-in user — this is what
    // makes "Sign out" actually leave the page.
    if (!loading && !user) {
        return <Navigate to="/login" replace />;
    }

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[var(--color-primary-400)]">
            <div className="w-full max-w-md">
                <div className="bg-[var(--bg-cream)] rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.18)] px-7 py-9 sm:px-10 sm:py-11">
                    <div className="flex flex-col items-center text-center">
                        <img
                            src="/savorygrimoire-512.png"
                            alt=""
                            aria-hidden="true"
                            className="h-24 w-auto mb-4 select-none"
                        />
                        <h1 className="font-display text-xl sm:text-2xl tracking-[0.08em] text-[var(--fg-1)]">
                            The Savory Grimoire
                        </h1>
                    </div>

                    <div className="flex items-center justify-center gap-4 my-7" aria-hidden="true">
                        <span className="h-px flex-1 bg-[var(--hairline)]" />
                        <span className="text-lg leading-none text-[var(--color-accent)] select-none">❦</span>
                        <span className="h-px flex-1 bg-[var(--hairline)]" />
                    </div>

                    <div className="text-center">
                        <h2 className="font-serif text-lg sm:text-xl text-[var(--fg-1)]">
                            You don't have access yet
                        </h2>
                        <p className="mt-3 font-serif text-sm sm:text-base leading-relaxed text-[var(--fg-muted)]">
                            You're signed in, but you haven't been given access yet. Ask
                            whoever brought you here to add you, then sign in again.
                        </p>
                    </div>

                    <Button
                        variant="tertiary"
                        onPress={handleSignOut}
                        isPending={signingOut}
                        className="w-full mt-8"
                    >
                        {({ isPending }: { isPending: boolean }) =>
                            isPending ? "Signing out..." : "Sign out"
                        }
                    </Button>
                </div>
            </div>
        </div>
    );
};
